// Story 8.3 (ADO-2012), Task 11.5: where a line a colleague removed is drawn (card 03). The
// Removed Line goes back after the line it followed when the Shopper saw it; if that line is
// gone too, it goes last.
//
// The side panel lists the store's lines, and the store keeps, with the conflict, the line
// the removed one followed. The review page groups its lines by department and re-reads the
// review once the line is gone, so it keeps its own memory of where each line sat
// (`reviewPlacements`); a department left empty comes back as an empty band in its place, as
// the frames draw "Bakery 0% of value 0 cases · €0.00".

import type { BasketReviewGroup } from '../api/basket-review.client';
import { conflictView, type ConflictViewAction } from './conflictCopy';
import type { LineConflict } from './useBasketStore';

export interface RemovedEntry {
  readonly kind: 'removed';
  readonly skuCode: string;
  /** The name the Shopper saw, or the code when the row showed none. */
  readonly name: string;
  readonly view: { readonly message: string; readonly action: ConflictViewAction };
  /** False for a refused send's mark (card 09), which the banner announces, not each line. */
  readonly announce: boolean;
}

export type LineEntry<Line> = { readonly kind: 'line'; readonly line: Line } | RemovedEntry;

/** The Removed Line for a conflict, or null when it draws none (a line still in the cart, a
 *  changed line, or both removing the same line, OQ-3a). */
function removedEntry(skuCode: string, conflict: LineConflict): RemovedEntry | null {
  const view = conflictView(conflict);
  if (view.kind !== 'removed') return null;
  return {
    kind: 'removed',
    skuCode,
    name: conflict.lastShown?.name ?? skuCode,
    view: { message: view.message, action: view.action },
    announce: conflict.action !== 'review',
  };
}

function insertAfter<Line>(
  entries: LineEntry<Line>[],
  entry: RemovedEntry,
  after: string | null,
  skuOf: (line: Line) => string,
): void {
  if (after === null) {
    entries.unshift(entry);
    return;
  }
  const index = entries.findIndex(
    (candidate) =>
      (candidate.kind === 'line' ? skuOf(candidate.line) : candidate.skuCode) === after,
  );
  if (index === -1) entries.push(entry);
  else entries.splice(index + 1, 0, entry);
}

/** The side panel's lines with each Removed Line where it was. */
export function withRemovedLines<Line extends { readonly skuCode: string }>(
  lines: readonly Line[],
  conflicts: ReadonlyMap<string, LineConflict>,
): LineEntry<Line>[] {
  const entries: LineEntry<Line>[] = lines.map((line) => ({ kind: 'line', line }));
  const inCart = new Set(lines.map((line) => line.skuCode));
  for (const [skuCode, conflict] of conflicts) {
    if (inCart.has(skuCode)) continue;
    const entry = removedEntry(skuCode, conflict);
    if (entry === null) continue;
    insertAfter(entries, entry, conflict.lastShown?.after ?? null, (line) => line.skuCode);
  }
  return entries;
}

/** Where a line sat on the review: its department, the line before it there (`null`: it was
 *  first), and the department's place among the groups. */
export interface ReviewPlacement {
  readonly departmentName: string | null;
  readonly after: string | null;
  readonly groupIndex: number;
}

/** Where each line of `groups` sits, plus the remembered place of each line no longer there
 *  whose conflict is still shown (`keep`); any other remembered line is forgotten. */
export function reviewPlacements(
  groups: readonly {
    readonly departmentName: string | null;
    readonly lines: readonly { readonly skuCode: string }[];
  }[],
  previous: ReadonlyMap<string, ReviewPlacement>,
  keep: ReadonlySet<string>,
): Map<string, ReviewPlacement> {
  const placements = new Map<string, ReviewPlacement>();
  groups.forEach((group, groupIndex) => {
    group.lines.forEach((line, index) => {
      if (placements.has(line.skuCode)) return;
      placements.set(line.skuCode, {
        departmentName: group.departmentName,
        after: group.lines[index - 1]?.skuCode ?? null,
        groupIndex,
      });
    });
  });
  for (const skuCode of keep) {
    const remembered = previous.get(skuCode);
    if (!placements.has(skuCode) && remembered !== undefined) placements.set(skuCode, remembered);
  }
  return placements;
}

export interface ReviewGroupEntries<Row> {
  readonly group: BasketReviewGroup;
  readonly entries: LineEntry<Row>[];
}

/** The review's groups with each Removed Line in its department, after the line before it. */
export function withRemovedReviewLines<Row extends { readonly line: { readonly skuCode: string } }>(
  rows: readonly { readonly group: BasketReviewGroup; readonly lines: readonly Row[] }[],
  conflicts: ReadonlyMap<string, LineConflict>,
  placements: ReadonlyMap<string, ReviewPlacement>,
): ReviewGroupEntries<Row>[] {
  const groups: ReviewGroupEntries<Row>[] = rows.map(({ group, lines }) => ({
    group,
    entries: lines.map((line) => ({ kind: 'line', line })),
  }));
  const shown = new Set(rows.flatMap(({ lines }) => lines.map((row) => row.line.skuCode)));
  for (const [skuCode, conflict] of conflicts) {
    if (shown.has(skuCode)) continue;
    const placement = placements.get(skuCode);
    const entry = removedEntry(skuCode, conflict);
    if (placement === undefined || entry === null) continue;
    let target = groups.find(
      (candidate) => candidate.group.departmentName === placement.departmentName,
    );
    if (target === undefined) {
      target = {
        group: { departmentName: placement.departmentName, caseCount: 0, lines: [] },
        entries: [],
      };
      groups.splice(Math.min(placement.groupIndex, groups.length), 0, target);
    }
    insertAfter(target.entries, entry, placement.after, (row) => row.line.skuCode);
  }
  return groups;
}
